import { useState } from "react";
import { ApiError } from "../lib/api";
import { useSuspendRiskCases } from "../lib/queries";
import type { BatchOutcome, RiskCase } from "../lib/types";
import { Modal } from "./Modal";
import { OtpCells } from "./OtpCells";
import { Alert, Button, Field, Textarea } from "./ui";

/**
 * Suspends the organizations behind several cases, from the queue.
 *
 * The same suspension `SuspendOrgModal` applies to one tenant — read-only for
 * members, workloads left running and billing, the reason shown to them and
 * mailed — with two differences, both because it is many. The reason is
 * required: a batch mails every one of them, and "no reason given" twenty
 * times is twenty tickets. And the operator's authenticator is asked for, on
 * the request itself, the way a delete asks: a console left open should not be
 * able to lock a page of tenants out on its own.
 *
 * An organization that is already suspended is listed and left alone. The API
 * would refuse it anyway — a batch never rewrites a suspension somebody else
 * decided, or one for credit — and saying so before the button is pressed
 * beats reporting it after.
 */
export function SuspendCasesModal({
  open,
  cases,
  onClose,
  onDone,
}: {
  open: boolean;
  cases: RiskCase[];
  onClose: () => void;
  /** After the request answered, with what happened to each case. */
  onDone: (outcomes: BatchOutcome[]) => void;
}) {
  const suspend = useSuspendRiskCases();
  const [reason, setReason] = useState("");
  const [blockPayment, setBlockPayment] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);

  const held = cases.filter((c) => c.organization.suspendedAt);
  const count = cases.length - held.length;
  const ready = count > 0 && reason.trim().length > 0 && otp.length === 6;

  function close() {
    if (suspend.isPending) return;
    setReason("");
    setBlockPayment(false);
    setOtp("");
    setError(null);
    onClose();
  }

  async function submit() {
    setError(null);
    try {
      const response = await suspend.mutateAsync({
        // All of them, the held ones included, so the result names them too.
        ids: cases.map((c) => c.id),
        reason: reason.trim(),
        blockPayment,
        otp,
      });
      setReason("");
      setBlockPayment(false);
      setOtp("");
      onDone(response.data);
    } catch (cause) {
      // A refused code is cleared: the next one is a different code.
      if (cause instanceof ApiError && cause.code === "totp_required") setOtp("");
      setError(cause instanceof Error ? cause.message : "The request could not be sent.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Suspend ${count} organization${count === 1 ? "" : "s"}`}
      description="Members keep read access, support and top-ups. Deploys and edits are blocked. Workloads are not stopped — they keep running and keep billing. Each case closes as suspended."
      dismissable={!suspend.isPending}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) void submit();
        }}
        className="space-y-4"
      >
        {error && <Alert>{error}</Alert>}

        <ul className="max-h-40 divide-y divide-line overflow-y-auto rounded-md border border-line text-sm">
          {cases.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate text-fg">{c.organization.name}</span>
              {c.organization.suspendedAt ? (
                <span className="shrink-0 text-xs text-fg-subtle">already suspended — left alone</span>
              ) : (
                <span className="shrink-0 text-xs text-fg-subtle tabular-nums">score {c.score}</span>
              )}
            </li>
          ))}
        </ul>

        {/* What a batch is for, where the decision is made. The Acceptable Use
            Policy promises a tenant whose breach is not severe a warning and a
            chance to put it right first; a batch warns nobody, so it is for the
            severe kind only, and says so. */}
        <p className="text-xs leading-5 text-fg-subtle">
          For one abuser under several names, or harm in progress across all of them. Where a breach
          is not severe the Acceptable Use Policy promises a warning first, and a batch warns nobody —
          decide those one at a time.
        </p>

        <Field
          label="Reason"
          htmlFor="bulk-suspend-reason"
          hint="Shown to the members of every one of them when they try a blocked action, and mailed to their owners and admins."
        >
          <Textarea
            id="bulk-suspend-reason"
            rows={3}
            maxLength={2000}
            placeholder="Cryptocurrency mining, which the Acceptable Use Policy does not allow."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </Field>

        {/* The same switch, and the same default, as one tenant's suspension. */}
        <label className="flex items-start gap-2.5 text-sm text-fg">
          <input
            type="checkbox"
            className="mt-0.5 size-3.5 accent-accent"
            checked={blockPayment}
            onChange={(e) => setBlockPayment(e.target.checked)}
          />
          <span>
            Refuse top-ups too
            <span className="mt-0.5 block text-xs text-fg-subtle">
              For suspected fraud, a chargeback or a sanctions concern. Otherwise leave this off: their
              workloads keep running and keep drawing credit, so they need a way to pay for what they
              are still being served.
            </span>
          </span>
        </label>

        <Field
          label="Authenticator code"
          htmlFor="bulk-suspend-otp"
          hint="The six digits from your authenticator app, right now. Six wrong codes in fifteen minutes lock this for a while."
        >
          <OtpCells id="bulk-suspend-otp" value={otp} onChange={setOtp} disabled={suspend.isPending} />
        </Field>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={close} disabled={suspend.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={!ready} loading={suspend.isPending}>
            Suspend {count}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
